// frontend/src/components/CallLogs.js 

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../api'; // Changed import from 'axios' to 'api'
import { 
   User,  ChevronRight, 
  Search,  DownloadCloud, RefreshCw,
  AlertCircle,  DollarSign,AlertTriangle , PhoneIncoming 
} from 'lucide-react';
import { Container } from './ui/Container';
import { GradientButton } from './ui/GradientButton';
import { useTranslation } from 'react-i18next';
import { io } from 'socket.io-client';


const SOCKET_IO_URL = process.env.REACT_APP_SOCKET_IO_URL || 'http://localhost:5000';

const CallLogs = () => {
  // State management
  const [callLogs, setCallLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [socket, setSocket] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('desc');
  const [timeframe, setTimeframe] = useState('24h');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [logsPerPage] = useState(100);
  const { t } = useTranslation();

  
  const getStatusStyles = (status) => {
    switch (status.toLowerCase()) {
      case 'answered':
        return 'bg-green-100 text-green-800';
      case 'booking':
        return 'bg-purple-100 text-purple-800';
      case 'complaint':
        return 'bg-red-100 text-red-800';
      case 'emergency':
        return 'bg-orange-100 text-orange-800';
      case 'sales':
        return 'bg-blue-100 text-blue-800';
      case 'appointment':
        return 'bg-yellow-100 text-yellow-800';
      case 'billing':
        return 'bg-teal-100 text-teal-800';
      case 'information':
        return 'bg-indigo-100 text-indigo-800';
      case 'quote':
        return 'bg-cyan-100 text-cyan-800';
      case 'consultation':
        return 'bg-pink-100 text-pink-800';
      case 'follow-up':
        return 'bg-amber-100 text-amber-800';
      case 'unknown':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  // Socket.io setup
  useEffect(() => {
    const newSocket = io(SOCKET_IO_URL);
    setSocket(newSocket);

    newSocket.on('new-call', (newCall) => {
       console.log('REFECTOR');
      setCallLogs((prevCallLogs) => [newCall, ...prevCallLogs]);
    });

    newSocket.on('call-update', (updatedCall) => {
      setCallLogs((prevCallLogs) =>
        prevCallLogs.map((call) =>
          call._id === updatedCall._id ? { ...call, ...updatedCall } : call
        )
      );
    });

    return () => newSocket.close();
  }, []);

  // Data fetching
  useEffect(() => {
    const fetchCallLogs = async () => {
      try {
        const res = await api.get('/call-logs'); // Changed from 'axios.get' to 'api.get'
        setCallLogs(res.data);
      } catch (err) {
        console.error('Error fetching call logs:', err);
        setError(t('CallLogs.error.message'));
      } finally {
        setLoading(false);
      }
    };

    fetchCallLogs();
  }, []);

  // Refresh function
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const res = await api.get('/call-logs'); // Changed from 'axios.get' to 'api.get'
      setCallLogs(res.data);
    } catch (err) {
      console.error('Error refreshing call logs:', err);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Filter logs
  const filteredLogs = callLogs
    .filter(call => {
      if (searchQuery) {
        return (
          call.callerName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          call.phoneNumber?.includes(searchQuery)
        );
      }
      return true;
    })
    .filter(call => {
      if (selectedFilter === 'all') {
        return true;
      }
      return call.status.toLowerCase() === selectedFilter.toLowerCase();
    })
    .sort((a, b) => {
      return sortOrder === 'desc' 
        ? new Date(b.timestamp) - new Date(a.timestamp)
        : new Date(a.timestamp) - new Date(b.timestamp);
    });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header with Stats and Filters */}
      <div className="bg-white border-b border-gray-200">
        <Container className="py-6 px-4 sm:px-6 lg:px-20">
          {/* Header Title and Buttons */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="mb-4 lg:mb-0">
              <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">
                {t('CallLogs.title')}
              </h1>
              <p className="mt-2 text-sm text-gray-600">
                {t('CallLogs.description')}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <GradientButton
                onClick={handleRefresh}
                className="inline-flex items-center"
                disabled={isRefreshing}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                {isRefreshing ? t('CallLogs.refresh.loading') : t('CallLogs.refresh.label')}
              </GradientButton>
              
              <button
                className="inline-flex items-center px-4 py-2 border border-gray-300 
                  rounded-xl text-sm font-medium text-gray-700 bg-white 
                  hover:bg-gray-50 focus:outline-none focus:ring-2 
                  focus:ring-offset-2 focus:ring-violet-500"
              >
                <DownloadCloud className="w-4 h-4 mr-2" />
                {t('CallLogs.export_logs')}
              </button>
            </div>
          </div>

          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
          <motion.div
  initial={{ opacity: 0, y: 20 }}
  animate={{ opacity: 1, y: 0 }}
  className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm"
>
  <div className="flex items-center">
    <div className="p-2 bg-violet-50 rounded-lg">
      <PhoneIncoming className="w-6 h-6 text-violet-600" />
    </div>
    <div className="ml-4">
      <p className="text-sm font-medium text-gray-600">
        {t('CallLogs.stats.total_calls')}
      </p>
      <p className="text-2xl font-semibold text-gray-900">
        {callLogs.length}
      </p>
    </div>
  </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm"
          >
            <div className="flex items-center">
              <div className="p-2 bg-orange-50 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-orange-600" />

              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  {t('CallLogs.stats.emergency_calls')}
                </p>
                <p className="text-2xl font-semibold text-gray-900">
                  {callLogs.filter(call => call.status.toLowerCase() === 'emergency').length}
                </p>
              </div>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm"
          >
            <div className="flex items-center">
              <div className="p-2 bg-blue-50 rounded-lg">
              <DollarSign className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  {t('CallLogs.stats.sales_calls')}
                </p>
                <p className="text-2xl font-semibold text-gray-900">
                  {callLogs.filter(call => call.status.toLowerCase() === 'sales').length}
                </p>
              </div>
            </div>
          </motion.div>

          </div>

          {/* Filters and Search */}
          <div className="mt-6 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex-1 flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-xl
                    text-sm placeholder-gray-500 focus:outline-none focus:ring-1 
                    focus:ring-violet-500 focus:border-violet-500"
                  placeholder={t('CallLogs.search_placeholder')}
                />
              </div>

              <div className="flex items-center space-x-4">
              <select
                value={selectedFilter}
                onChange={(e) => setSelectedFilter(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border 
                  border-gray-300 focus:outline-none focus:ring-violet-500 
                  focus:border-violet-500 sm:text-sm rounded-xl"
              >
                <option value="all">{t('CallLogs.filters.all_calls')}</option>
                <option value="emergency">{t('CallLogs.filters.emergency_calls')}</option>
                <option value="sales">{t('CallLogs.filters.sales_calls')}</option>
                <option value="booking">{t('CallLogs.filters.booking_calls')}</option>
                <option value="complaint">{t('CallLogs.filters.complaint_calls')}</option>
                <option value="information">{t('CallLogs.filters.information_calls')}</option>
              </select>

                <select
                  value={timeframe}
                  onChange={(e) => setTimeframe(e.target.value)}
                  className="block w-full pl-3 pr-10 py-2 text-base border 
                    border-gray-300 focus:outline-none focus:ring-violet-500 
                    focus:border-violet-500 sm:text-sm rounded-xl"
                >
                  <option value="24h">{t('CallLogs.timeframe.last_24_hours')}</option>
                  <option value="7d">{t('CallLogs.timeframe.last_7_days')}</option>
                  <option value="30d">{t('CallLogs.timeframe.last_30_days')}</option>
                  <option value="all">{t('CallLogs.timeframe.all_time')}</option>
                </select>
              </div>
            </div>
          </div>
        </Container>
      </div>

      {/* Table Section */}
      <Container className="py-6 px-4 sm:px-6 lg:px-20">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600" />
            <span className="ml-3 text-sm text-gray-600">{t('CallLogs.loading_message')}</span>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">{t('CallLogs.error.title')}</h3>
                <p className="mt-1 text-sm text-red-600">{error}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('CallLogs.table.headers.caller_info')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('CallLogs.table.headers.call_time')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('CallLogs.table.headers.status')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('CallLogs.table.headers.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  <AnimatePresence>
                    {filteredLogs.map((call) => (
                      <motion.tr
                        key={call._id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="hover:bg-gray-50"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-violet-100 flex items-center justify-center">
                                <User className="h-5 w-5 text-violet-600" />
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {call.callerName || t('CallLogs.table.unknown')}
                              </div>
                              <div className="text-sm text-gray-500">
                                {call.phoneNumber || t('CallLogs.table.unknown')}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {new Date(call.timestamp).toLocaleDateString()}
                          </div>
                          <div className="text-sm text-gray-500">
                            {new Date(call.timestamp).toLocaleTimeString()}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                            ${getStatusStyles(call.status)}`}>
                            {t(`CallLogs.status.${call.status.toLowerCase()}`) || call.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <Link
                            to={`/call-details/${call._id}`}
                            className="inline-flex items-center px-3 py-2 border border-violet-100 
                              rounded-lg text-sm text-violet-700 bg-violet-50 hover:bg-violet-100"
                          >
                            {t('CallLogs.table.view_details')}
                            <ChevronRight className="ml-2 h-4 w-4" />
                          </Link>
                        </td>
                      </motion.tr>
                    ))}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Container>
    </div>
  );
};

export default CallLogs;
